import { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { API_LABEL, type VolumeTrend } from "../../model";
import { formatTrendValue } from "../trendChart";

interface PartnerTrendChartProps {
  trend: VolumeTrend;
}

const CHART_HEIGHT = 220;
const X_LABEL_STEP = 5;
/** Caps bar thickness at <=24px (mark spec) for 30 day-groups regardless of viewport width. */
const CHART_MAX_WIDTH = 900;

/**
 * The deep dive's 30-day Saved vs Ad Hoc trend chart (CPD-71) — a grouped
 * `@mui/x-charts` `BarChart`, the same charting library twosion-ui and
 * insights-home already standardize on (`AppBarChart.tsx` / `CoverageChart.tsx`),
 * rather than a one-off hand-rolled SVG. Colors come from the theme's
 * primary/secondary tokens, validated with `validate_palette.js`: dark mode
 * uses `primary.light` (the dark theme's own `main` pair fails the OKLCH
 * lightness band); `secondary.dark` is used in both modes, since the light
 * theme's `secondary.main` only clears 2.74:1 against the white chart
 * surface — short of WCAG 1.4.11's 3:1 non-text-contrast floor.
 *
 * Defaults to the table view below the `sm` breakpoint — 30 day-groups of 2
 * bars each get too thin to read or tap once the chart's width is a phone
 * screen's, the same problem `PartnerMetricsTable`/`PartnerActivityTable`
 * solved by auto-swapping to a mobile-friendly view (CPD-67/CPD-70). Unlike
 * those, the toggle here stays available either way, since both views are
 * already required by the dataviz skill's accessibility pass — mobile just
 * changes which one loads first.
 */
export function PartnerTrendChart({ trend }: PartnerTrendChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showTable, setShowTable] = useState(isMobile);

  const savedColor =
    theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main;
  // `secondary.dark` in both modes, not `secondary.main` in light mode: the
  // light theme's secondary.main (#78A842) only clears 2.74:1 against the
  // white chart surface — below WCAG 1.4.11's 3:1 non-text-contrast floor.
  // secondary.dark clears >=3:1 in both modes with no other check regressing.
  const adhocColor = theme.palette.secondary.dark;
  const tickLabelStyle = { fontSize: 11, fill: theme.palette.text.secondary };

  return (
    <Stack spacing={1} role="region" aria-label="30-day trend chart">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">30-Day Trend</Typography>
        <Button size="small" onClick={() => setShowTable((current) => !current)}>
          {showTable ? "View as chart" : "View as table"}
        </Button>
      </Stack>

      {showTable ? (
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader aria-label="30-day trend">
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell align="right">{API_LABEL.saved}</TableCell>
                <TableCell align="right">{API_LABEL.adhoc}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trend.map((d) => (
                <TableRow key={d.day}>
                  <TableCell>Day {d.day}</TableCell>
                  <TableCell align="right">{formatTrendValue(d.saved)}</TableCell>
                  <TableCell align="right">{formatTrendValue(d.adhoc)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Capped so bars stay <=24px thick per the mark spec regardless of
        // viewport width — 30 day-groups over this width keeps each of the
        // two per-day bars comfortably under the cap, even on a wide screen.
        <Box sx={{ maxWidth: CHART_MAX_WIDTH }}>
          <BarChart
            height={CHART_HEIGHT}
            borderRadius={4}
            margin={{ left: 48, right: 16, top: 16, bottom: 28 }}
            grid={{ horizontal: true }}
            slotProps={{ tooltip: { trigger: "axis" } }}
            series={[
              {
                id: "saved",
                label: API_LABEL.saved,
                data: trend.map((d) => d.saved),
                color: savedColor,
                valueFormatter: formatTrendValue,
              },
              {
                id: "adhoc",
                label: API_LABEL.adhoc,
                data: trend.map((d) => d.adhoc),
                color: adhocColor,
                valueFormatter: formatTrendValue,
              },
            ]}
            xAxis={[
              {
                scaleType: "band",
                data: trend.map((d) => d.day),
                valueFormatter: (day: number) => `D${day}`,
                tickInterval: (_value, index) =>
                  index % X_LABEL_STEP === 0 || index === trend.length - 1,
                disableTicks: true,
                disableLine: true,
                tickLabelStyle,
              },
            ]}
            yAxis={[
              {
                disableTicks: true,
                disableLine: true,
                valueFormatter: (value: number) => value.toLocaleString(),
                tickLabelStyle,
              },
            ]}
          />
        </Box>
      )}
    </Stack>
  );
}
